
import { Avatar, Button, Card, CardActions, CardContent, Chip, Typography, styled, Box, Alert } from "@mui/material"
import { useEffect, useState, useMemo } from "react";
import SkeletonCard from "../../components/Loaders/SkeletonCard";
import { useDispatch, useSelector } from "react-redux";
import { fetchAvailabilitiesByScheduleId } from "../../redux/actions/apiActions";
import GridContainer from "../../components/Grid/GridContainer";
import GridItem from "../../components/Grid/GridItem";
import SlotPicker from "../../components/SlotPicker/SlotPicker";
import { DoubleArrow, LocationOn, LocationOnOutlined, Public, VideoCall } from "@mui/icons-material";
import { defaultProviderImageUrl, defaultServiceImageUrl } from "../../config/constants";
import { setAssesmentData, setSelectedAvailability, setShowUserForm } from "../../redux/actions/commonActions";
import LocationModal from "../../components/LocationModal/LocationModal";
import CustomFixedButton from "../../components/CustomButtons/CustomFixedButton";
import SurveyModal from "../../components/SurveyModal/SurveyModal";
import moment from "moment-timezone";
import { formatPrice } from "../../utils/misc";
import RatingsAndReviews from "./RatingsAndReviews";
import RecurringBookingAlert from "../../components/Alert/RecurringBookingAlert";


const RibbonContainer = styled(Box)(({ theme }) => ({
    backgroundColor: "green",
    position: "absolute",
    color: theme.palette.primary.contrastText,
    width: 180,
    zIndex: 3,
    textAlign: "center",
    padding: 3,
    "&::before": {
        position: "absolute",
        zIndex: -1,
        display: "block",
        border: "5px solid #2980b9",
    },
    "&::after": {
        position: "absolute",
        zIndex: -1,
        display: "block",
        border: "5px solid #2980b9",
    },
    transform: "rotate(-45deg)",
    top: 35,
    marginLeft: -40,
}));
const RibbonText = styled(Typography)(({ theme }) => ({
    fontSize: 16
}));

const ScheduleCard = ({ serviceData, scheduleData, providerList, isLoading }) => {

    const [selectedSlot, setSelectedSlot] = useState(null);
    const availabilities = useSelector(state => state.api.availabilities[scheduleData.id]);
    const availabilitiesLoading = useSelector(state => state.api.availabilitiesLoading[scheduleData.id]);
    const [isCounterTypeService, setIsisCounterTypeService] = useState(false);
    const [providerData, setProviderData] = useState({});
    const productDetails = useSelector(state => state.api.productDetails);

    const [filteredAvailabilities, setFilteredAvailabilities] = useState([]);

    const dispatch = useDispatch();

    const [showLocation, setShowLocation] = useState(false);
    const [showAdditionalInfoModal, setShowAdditionalInfoModal] = useState(false);
    const [locationCoords, setLocationCoords] = useState(null);

    const openLocationModal = () => {
        if (scheduleData?.geoLocation?.coordinates) {
            let coordinates = scheduleData?.geoLocation?.coordinates;
            if (coordinates?.latitude && coordinates?.longitude) {
                setShowLocation(true);
                setLocationCoords(coordinates);
            }
        }
    }

    const onCloseLocationModal = () => {
        setShowLocation(false);
        setLocationCoords(null);
    }

    const onSelectSlot = (slot) => {
        if (scheduleData?.multiBooking?.minSlots) {
            setSelectedSlot(prev => {
                const safePrev = prev ?? [];
                // If slot already selected, remove it
                if (safePrev.map(item => item.id).includes(slot.id)) {
                    return safePrev.filter(item => item.id != slot.id);
                }
                // If under maxSlots, add and sort by startSec
                return safePrev.length < (scheduleData?.multiBooking?.maxSlots ?? scheduleData?.multiBooking?.packageLimit ?? Infinity)
                    ? [...safePrev, slot].sort((a, b) => a.startSec - b.startSec)
                    : safePrev;
            });
        } else {
            setSelectedSlot(prev => prev?.startSec !== slot?.startSec ? slot : null)
        }
    }
    const onJoinQueue = (slot) => {
        setSelectedSlot(prev => prev?.startSec !== slot?.startSec ? slot : null)
        onClickSlotConfirm(slot);
    }

    const onClickSlotConfirm = (slot) => {
        const selectedSlot_ = slot ?? selectedSlot;
        const data = { scheduleData, selectedSlot: selectedSlot_ }
        dispatch(setSelectedAvailability(selectedSlot_));
        if (scheduleData?.surveyId) {
            setShowAdditionalInfoModal(true);
        } else {
            dispatch(setShowUserForm(data));
            setSelectedSlot(null);
        }
    }

    const onCompleteAdditionalInfo = (surveyData) => {
        if (surveyData.data) {
            const sanitizedData = surveyData.data;
            setShowAdditionalInfoModal(false);
            let assesmentData = {
                eventId: null,
                participantId: null,
                questionAnswers: JSON.stringify(sanitizedData),
                status: "COMPLETED",
                surveyId: scheduleData.surveyId
            }
            dispatch(setAssesmentData(assesmentData));
            const data = { scheduleData, selectedSlot }
            dispatch(setShowUserForm(data));
            setSelectedSlot(null);
        }
    }

    useEffect(() => {
        if (scheduleData?.id) {
            dispatch(fetchAvailabilitiesByScheduleId(scheduleData?.id))
        }
    }, [scheduleData])

    useEffect(() => {
        if (scheduleData?.providerId && providerList?.length) {
            let providerData_ = providerList.find(provider => provider.id === scheduleData?.providerId);
            setProviderData(providerData_);
        }
    }, [scheduleData, providerList])

    useEffect(() => {
        if (serviceData?.id) {
            let isCounterTypeService_ = serviceData?.counters && serviceData?.counters?.length > 0 ? true : false;
            setIsisCounterTypeService(isCounterTypeService_);
        }
    }, [serviceData])

    useEffect(() => {
        if (serviceData?.mode === 'CALLBACK') {
            /* Load survey/Form for taking Callback Contact details*/
        }
        if (Array.isArray(selectedSlot) && selectedSlot.length) {
            const firstSlotSec = selectedSlot[0]?.startSec;
            const horizonDays = scheduleData?.multiBooking?.horizonDays ?? 2;
            const gapMinutes = scheduleData?.multiBooking?.gapMinutes ?? 0;
            const maxSlots = scheduleData?.multiBooking?.maxSlots ?? scheduleData?.multiBooking?.packageLimit ?? Infinity;
            const selectedSlotIds = selectedSlot.map(item => item.id);
            const gapInSec = gapMinutes * 60;

            const horizonEndSec = moment(firstSlotSec * 1000)
                .add(horizonDays, "days")
                .endOf("day")
                .unix();

            let filteredAvailabilities = availabilities
                .filter(item => item.startSec < horizonEndSec)
                .map(item => {
                    const isSelected = selectedSlotIds.includes(item.id);
                    let disabled = false;
                    if (selectedSlot.length >= maxSlots) {
                        disabled = !isSelected;
                    } else {
                        disabled = selectedSlot.some(sel => {
                            const selStart = sel.startSec;
                            return (
                                !isSelected &&
                                item.startSec > selStart - gapInSec &&
                                item.startSec < selStart + gapInSec
                            );
                        });
                    }
                    return { ...item, disabled };
                });

            setFilteredAvailabilities(filteredAvailabilities);
        } else {
            setFilteredAvailabilities(availabilities);
        }
    }, [availabilities, selectedSlot, scheduleData])

    const getSlotSelectionMessage = (minSlots, maxSlots, packageLimit, selectedCount) => {
        const totalAllowed = packageLimit ?? maxSlots;
        const remaining = Math.max(totalAllowed - selectedCount, 0);

        const plural = (count, word = "slot") => `${count} ${word}${count === 1 ? "" : "s"}`;

        // Single slot case
        if (maxSlots === 1) {
            return selectedCount === 0 ? "Please select the slot." : "You’ve selected the slot.";
        }
        // If user selected more than allowed
        if (selectedCount > maxSlots) {
            return `You can select a maximum of ${plural(maxSlots)}.`;
        }
        // Not reached minimum requirement
        if (selectedCount < minSlots) {
            if (minSlots === maxSlots) {
                return `Please select ${plural(maxSlots)}.`;
            }
            const laterText = remaining > 0 ? ` ${plural(totalAllowed - minSlots)} can be booked later.` : "";
            return `Please select at least ${plural(minSlots)}.${laterText}`;
        }
        // Reached maximum
        if (selectedCount === maxSlots) {
            return `You’ve selected all ${plural(maxSlots)}.`;
        }
        // Between min and max
        if (selectedCount < maxSlots) {
            const laterText = remaining > 0 ? ` ${plural(remaining)} can be booked later.` : "";
            return `You’ve selected ${plural(selectedCount)} of ${plural(maxSlots)}.${laterText}`;
        }
        return "";
    };

    const pricedetails = useMemo(() => {
        if (!scheduleData?.serviceId) return null;
        const productPrice = productDetails?.services?.find(sv => sv.serviceId === scheduleData.serviceId)?.price;
        if (productPrice?.enablePayment) {
            return {
                amount: productPrice?.value ?? 0,
                currency: productPrice?.currency ?? "",
                ...productPrice
            };
        }
        if (scheduleData?.payment) {
            return {
                amount: scheduleData?.payment?.amount ?? 0,
                currency: scheduleData?.payment?.currency ?? "",
                ...scheduleData.payment
            };
        }
        return null;
    }, [scheduleData, productDetails])


    return (
        <>
            {isLoading ?
                <SkeletonCard cardType="schedule" />
                :
                <Card sx={{ borderRadius: 5, position: 'relative' }}>
                    {pricedetails?.amount ?
                        <RibbonContainer sx={{ boxShadow: 1 }}>
                            <RibbonText>{formatPrice(pricedetails?.amount ?? 0, pricedetails?.currency ?? "")} /-</RibbonText>
                        </RibbonContainer>
                        : null
                    }
                    <CardContent>
                        <GridContainer alignItems='flex-start'>
                            <GridItem xs={12} md={4} >
                                <div className="justify-center py-5">
                                    {isCounterTypeService ?
                                        <>
                                            <Avatar
                                                sx={{ width: 100, height: 100, '& img': { objectFit: 'contain' } }}
                                                alt={""}
                                                src={serviceData?.image?.fileUrl ? serviceData.image.fileUrl : defaultServiceImageUrl}
                                                className="mx-auto"
                                            />
                                            <p className="text-center whitespace-pre-wrap mx-auto mt-3">
                                                {`${serviceData?.name}`}
                                            </p>
                                            {serviceData?.description ?
                                                <p className="text-center whitespace-pre-wrap mx-auto mt-1">
                                                    {`${serviceData?.description}`}
                                                </p>
                                                : null}

                                        </>
                                        :
                                        <>
                                            <Avatar
                                                sx={{ width: 100, height: 100, '& img': { objectFit: 'contain' } }}
                                                alt={""}
                                                src={providerData?.providerInformation?.image?.fileUrl ? providerData.providerInformation?.image.fileUrl : defaultProviderImageUrl}
                                                className="mx-auto mb-3"
                                            />
                                            <p className="text-center whitespace-pre-wrap mx-auto ">{`${providerData?.designation ?? ''} ${providerData?.givenName ?? ''} ${providerData?.familyName ?? ''}`}</p>
                                            {providerData?.providerInformation?.qualifications ?
                                                <p className="text-center whitespace-pre-wrap mx-auto text-sm font-light" >{`${providerData?.providerInformation?.qualifications}`}</p>
                                                : null
                                            }
                                            {providerData?.providerInformation?.description ?
                                                <p className="text-center whitespace-pre-wrap mx-auto mt-1 text-sm font-extralight">
                                                    {`${providerData?.providerInformation?.description}`}
                                                </p>
                                                : null}
                                            {serviceData?.enableReviews &&
                                                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1, mx: 'auto' }}>
                                                    <RatingsAndReviews type="provider" details={providerData} />
                                                </Box>
                                            }
                                        </>
                                    }
                                    <div className="text-center mx-auto mt-5">
                                        {scheduleData?.description && scheduleData?.description.includes("TAGS;") && scheduleData?.description?.split(';')?.length ?
                                            scheduleData?.description?.split(';').map((tag, i) => i > 0 && tag.trim() &&
                                                <div style={{ padding: 3 }} className="text-center mx-auto" key={i}>
                                                    <Chip color={"info"} variant="outlined" label={tag} />
                                                </div>
                                            )
                                            :
                                            <Box>
                                                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", px: 2 }}>{scheduleData?.description ?? ''}</Typography>
                                            </Box>
                                        }
                                    </div>
                                    {scheduleData?.isOnline &&
                                        <div className="text-center mx-auto mt-1">
                                            <Button sx={{ textTransform: "none" }} startIcon={<VideoCall />}>
                                                {scheduleData?.onlineMeetingProvider == "Quezone" ? "Online Meeting"
                                                    : scheduleData?.onlineMeetingProvider == "MsTeams" ? "MS Teams"
                                                        : scheduleData?.onlineMeetingProvider == "Skype" ? "Skype"
                                                            : scheduleData?.onlineMeetingProvider == "GoogleMeet" ? "Google Meet"
                                                                : 'Online Meeting'
                                                }
                                            </Button>
                                        </div>
                                    }
                                    <div className="text-center mx-auto mt-3">
                                        <Button sx={{ textTransform: "none" }} onClick={openLocationModal}>
                                            <LocationOn fontSize="small" /><p>{` ${scheduleData?.geoLocation?.fullAddress}`}</p>
                                        </Button>
                                    </div>
                                    {/*
                                    <div className="text-center mx-auto">
                                        <Button sx={{ textTransform: "none" }}>
                                            <Public fontSize="small" /><p>{` ${scheduleData.timezoneId}`}{(moment.tz(scheduleData?.timezoneId).zoneAbbr() && false ? " - " + moment.tz(scheduleData?.timezoneId).zoneAbbr() : '')}</p>
                                        </Button>
                                    </div>
                                    */}
                                </div>
                            </GridItem>
                            <GridItem xs={12} sm={12} md={8} className="pt-10">
                                {
                                    availabilitiesLoading || false ?
                                        <SkeletonCard cardType="availabilities" />
                                        :
                                        <>
                                            <SlotPicker
                                                availabilities={filteredAvailabilities}
                                                onSelectSlot={onSelectSlot}
                                                onJoinQueue={onJoinQueue}
                                                selectedSlotIds={Array.isArray(selectedSlot) ? selectedSlot.map(item => item.id) : selectedSlot?.id}
                                                timezoneId={scheduleData?.timezoneId}
                                                mode={serviceData?.mode}
                                            />
                                            {scheduleData?.recurringBookingPlan ?
                                                <>
                                                    <RecurringBookingAlert
                                                        recurringBookingPlan={scheduleData?.recurringBookingPlan}
                                                        alertProps={{ sx: { mt: 2, mb: 0 } }}
                                                        title="This Booking Will Repeat Automatically"
                                                        behaviour="before-booking"
                                                    />
                                                </>
                                                : null
                                            }
                                        </>
                                }
                            </GridItem>
                        </GridContainer>
                    </CardContent>
                    <CardActions disableSpacing>
                        {scheduleData.multiBooking && selectedSlot?.length ?
                            <Box sx={{ mx: 'auto' }}>
                                <CustomFixedButton
                                    variant="text"
                                    offsetBottom={40}
                                    style={{ borderRadius: 5, width: '100%', flexWrap: 'wrap', gap: 5, textTransform: 'none' }}
                                >
                                    {selectedSlot?.length &&
                                        <Alert
                                            sx={{
                                                width: '100%', justifyContent: 'center',
                                                alignItems: 'center', borderRadius: 3,
                                                fontSize: "0.8rem", padding: "2px 8px",
                                                "& .MuiAlert-icon": { fontSize: "1rem" },
                                                "& .MuiAlert-message": { padding: 0 }
                                            }}
                                            severity={selectedSlot?.length < scheduleData?.multiBooking?.minSlots ? "warning" : 'info'}
                                        >
                                            {getSlotSelectionMessage(scheduleData?.multiBooking?.minSlots, scheduleData?.multiBooking?.maxSlots, scheduleData?.multiBooking?.packageLimit, selectedSlot?.length ?? 0)}
                                        </Alert>
                                    }
                                    {selectedSlot.map(slot =>
                                        <Chip
                                            color={"slotPicker"}
                                            variant="filled"
                                            sx={{ height: 'auto', padding: "3px" }}
                                            label={
                                                <>
                                                    {moment(slot?.startSec * 1000).tz(scheduleData?.timezoneId).format("h:mm A, ")}
                                                    {moment(slot?.startSec * 1000).tz(scheduleData?.timezoneId).format("Do MMM")}
                                                </>
                                            }
                                            onDelete={() => setSelectedSlot(prev => prev.filter(itm => itm.id !== slot.id))}
                                        />
                                    )}
                                </CustomFixedButton>
                                <CustomFixedButton
                                    disabled={Array.isArray(selectedSlot) && selectedSlot.length < scheduleData.multiBooking.minSlots}
                                    variant="contained"
                                    color="primary"
                                    style={{ borderRadius: 5, width: '100%', }}
                                    sx={{ "&.Mui-disabled": { backgroundColor: "#d3d3d3" } }}
                                    onClick={() => onClickSlotConfirm(selectedSlot)}
                                    endIcon={<DoubleArrow />}
                                >
                                    Confirm Booking
                                </CustomFixedButton>
                            </Box>

                            :
                            selectedSlot?.startSec && serviceData?.mode !== "QUEUE" ?
                                <GridContainer justifyContent="center">
                                    <CustomFixedButton variant="contained" color="primary" style={{ borderRadius: 5, width: '100%' }} onClick={() => onClickSlotConfirm(selectedSlot)} endIcon={<DoubleArrow />}>
                                        Confirm Booking
                                    </CustomFixedButton>
                                </GridContainer>
                                : null
                        }
                    </CardActions>
                </Card>
            }
            <LocationModal
                show={showLocation}
                onClose={onCloseLocationModal}
                coordinates={locationCoords}
                title={<> <LocationOnOutlined /> {scheduleData?.geoLocation?.fullAddress ?? ''}</>}
            />
            <SurveyModal
                show={showAdditionalInfoModal}
                onClose={() => setShowAdditionalInfoModal(false)}
                title="Provide Additional Information"
                surveyId={scheduleData?.surveyId}
                completeText="PROCEED TO BOOKING"
                onComplete={onCompleteAdditionalInfo}
            />
        </>
    )
}
export default ScheduleCard